import { apiFetchJson } from '../http/apiFetch';
import type {
  AdminDashboardCompany,
  AdminDashboardFilters,
  AdminDashboardSortKey,
  AdminDashboardSummary,
  SortDirection,
} from '../../models/adminDashboard';

function normalizeStatus(value: string | undefined): string {
  if (!value) {
    return 'Unknown';
  }
  return String(value).trim();
}

function isActiveStatus(status: string): boolean {
  const normalized = status.toLowerCase();
  return !['delivered', 'completed', 'cancelled', 'canceled', 'rejected', 'return-disposition-complete', 'returned-incidence', 'incidence', 'returned', 'return-in-progress'].includes(normalized);
}

function toNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function normalizeOrderCounts(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== 'object') {
    return {};
  }
  const entries = raw as Record<string, unknown>;
  return Object.entries(entries).reduce<Record<string, number>>((acc, [key, value]) => {
    acc[key] = toNumber(value);
    return acc;
  }, {});
}

function normalizeCompany(row: unknown): AdminDashboardCompany | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const company = row as Record<string, unknown>;
  const companyId = String(company.companyId ?? company.CompanyId ?? '').trim();
  const companyName = String(company.companyName ?? company.CompanyName ?? '').trim();
  if (!companyId || !companyName) {
    return null;
  }
  return {
    companyId,
    companyName,
    status: normalizeStatus(String(company.status ?? company.Status ?? 'Active')),
    merchantCount: toNumber(company.merchantCount ?? company.MerchantCount),
    orderCountsByStatus: normalizeOrderCounts(company.orderCountsByStatus ?? company.OrderCountsByStatus),
    createdAt: String(company.createdAt ?? company.CreatedAt ?? ''),
  };
}

export function buildAdminDashboardSummary(companies: AdminDashboardCompany[]): AdminDashboardSummary {
  const totalCompanies = companies.length;
  const activeCompanies = companies.filter((company) => company.status.toLowerCase() === 'active').length;
  const totalMerchants = companies.reduce((sum, company) => sum + company.merchantCount, 0);
  const activeOrders = companies.reduce((sum, company) => {
    return sum + Object.entries(company.orderCountsByStatus).reduce((orderSum, [status, count]) => {
      return orderSum + (isActiveStatus(status) ? count : 0);
    }, 0);
  }, 0);

  return {
    totalCompanies,
    activeCompanies,
    totalMerchants,
    activeOrders,
  };
}

export function filterAdminDashboardCompanies(
  companies: AdminDashboardCompany[],
  filters: AdminDashboardFilters
): AdminDashboardCompany[] {
  const normalizedStatus = (filters.status ?? '').trim().toLowerCase();
  const normalizedSearch = (filters.search ?? '').trim().toLowerCase();

  return companies.filter((company) => {
    const matchesStatus =
      !normalizedStatus || normalizedStatus === 'all' || company.status.toLowerCase() === normalizedStatus;
    const haystack = `${company.companyName} ${company.companyId}`.toLowerCase();
    const matchesSearch = !normalizedSearch || haystack.includes(normalizedSearch);
    return matchesStatus && matchesSearch;
  });
}

/** Total orders across ALL statuses for a company — this is the "mix" (every status counted). */
export function getCompanyOrderTotal(company: AdminDashboardCompany): number {
  return Object.values(company.orderCountsByStatus).reduce((sum, count) => sum + count, 0);
}

/**
 * Orders in a non-terminal status for a company, using the same `isActiveStatus` rule as
 * `buildAdminDashboardSummary`. This is what the "Active Orders" column/summary actually means —
 * kept distinct from `getCompanyOrderTotal` (all statuses) so the two never silently drift apart.
 */
export function getCompanyActiveOrderTotal(company: AdminDashboardCompany): number {
  return Object.entries(company.orderCountsByStatus).reduce((sum, [status, count]) => {
    return sum + (isActiveStatus(status) ? count : 0);
  }, 0);
}

function compareCompanies(
  a: AdminDashboardCompany,
  b: AdminDashboardCompany,
  key: AdminDashboardSortKey
): number {
  let left: string | number;
  let right: string | number;

  switch (key) {
    case 'merchantCount':
      left = a.merchantCount;
      right = b.merchantCount;
      break;
    // Sorts by non-terminal order count, matching what buildAdminDashboardSummary reports as
    // "activeOrders" (previously this compared getCompanyOrderTotal — the unfiltered total —
    // which could sort companies in an order inconsistent with the number shown in the summary).
    case 'activeOrders':
      left = getCompanyActiveOrderTotal(a);
      right = getCompanyActiveOrderTotal(b);
      break;
    // "Order Mix" = total orders across every status (the full mix), as opposed to activeOrders'
    // non-terminal-only count.
    case 'orderMix':
      left = getCompanyOrderTotal(a);
      right = getCompanyOrderTotal(b);
      break;
    case 'status':
      left = a.status.toLowerCase();
      right = b.status.toLowerCase();
      break;
    case 'createdAt':
      left = a.createdAt;
      right = b.createdAt;
      break;
    case 'companyName':
    default:
      left = a.companyName.toLowerCase();
      right = b.companyName.toLowerCase();
      break;
  }

  if (typeof left === 'string' && typeof right === 'string') {
    return left.localeCompare(right, undefined, { sensitivity: 'base' });
  }
  return Number(left) - Number(right);
}

/** Returns a new array sorted by the given key/direction. Does not mutate the input. */
export function sortAdminDashboardCompanies(
  companies: AdminDashboardCompany[],
  sortKey: AdminDashboardSortKey,
  sortDirection: SortDirection
): AdminDashboardCompany[] {
  return [...companies].sort((a, b) => {
    const result = compareCompanies(a, b, sortKey);
    return sortDirection === 'asc' ? result : -result;
  });
}

export function logAdminDashboardAudit(event: 'access' | 'filter', details: Record<string, unknown>): void {
  const payload = {
    event,
    timestamp: new Date().toISOString(),
    ...details,
  };

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('bo-admin-dashboard-audit', { detail: payload }));
  }
  console.info('[AdminDashboardAudit]', payload);
}

export async function fetchAdminDashboard(
  acquireAccessToken: () => Promise<string | undefined>,
  filters?: AdminDashboardFilters
): Promise<AdminDashboardCompany[]> {
  const params = new URLSearchParams();
  if (filters?.status && filters.status.toLowerCase() !== 'all') {
    params.set('status', filters.status);
  }
  if (filters?.search?.trim()) {
    params.set('search', filters.search.trim());
  }
  const query = params.toString();
  const path = `/api/admin/dashboard${query ? `?${query}` : ''}`;

  const result = await apiFetchJson<unknown>(path, {
    method: 'GET',
    getAccessToken: acquireAccessToken,
  });

  if (Array.isArray(result)) {
    return result.map(normalizeCompany).filter((item): item is AdminDashboardCompany => Boolean(item));
  }

  if (result && typeof result === 'object' && Array.isArray((result as { value?: unknown }).value)) {
    return ((result as { value: unknown[] }).value)
      .map(normalizeCompany)
      .filter((item): item is AdminDashboardCompany => Boolean(item));
  }

  return [];
}